import * as DialogPrimitive from '@radix-ui/react-dialog'
import { SearchIcon, TicketIcon, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { CounselProMark } from '@/components'
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/Dialog'
import {
  TicketsPane,
  useSupportTicket,
  useSupportUnreadCount,
} from '@/modules/support'
import { useCurrentSurface } from '../hooks/useCurrentSurface'
import { useHelpBot } from '../hooks/useHelpBot'
import { messageSources } from '../messageParts'
import { openHelp } from '../openHelp'
import {
  useHelpActions,
  useHelpInitialSlug,
  useHelpIsOpen,
  useHelpSeedQuestion,
  useHelpView,
} from '../store'
import type { HelpUIMessage } from '../types'
import { AnswerPane } from './AnswerPane'
import { HelpViewTabs } from './HelpViewTabs'
import { ReaderPane } from './ReaderPane'

function lastAssistant(messages: HelpUIMessage[]): HelpUIMessage | undefined {
  return [...messages].reverse().find((message) => message.role === 'assistant')
}

export function AskSurface() {
  const isOpen = useHelpIsOpen()
  const view = useHelpView()
  const seedQuestion = useHelpSeedQuestion()
  const initialSlug = useHelpInitialSlug()
  const { close, consumeSeed, setView } = useHelpActions()
  const { openTicket } = useSupportTicket()
  const unreadCount = useSupportUnreadCount()

  const surface = useCurrentSurface()
  const { messages, sendMessage, status, error, setMessages, stop } =
    useHelpBot()

  const [topInput, setTopInput] = useState('')
  const [activeSlug, setActiveSlug] = useState<string | null>(null)
  const autoOpenedTurn = useRef<string | null>(null)

  // Global Cmd/Ctrl-K toggles the surface.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        if (isOpen) close()
        else openHelp()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, close])

  // Consume a seeded question or article when the surface opens from context.
  useEffect(() => {
    if (!isOpen) return
    if (seedQuestion) sendMessage({ text: seedQuestion })
    if (initialSlug) setActiveSlug(initialSlug)
    if (seedQuestion || initialSlug) consumeSeed()
  }, [isOpen, seedQuestion, initialSlug, sendMessage, consumeSeed])

  // When an answer's sources arrive, open the top one in the reader — once per
  // turn, so a manual source pick afterwards is not overridden.
  useEffect(() => {
    const assistant = lastAssistant(messages)
    if (!assistant || autoOpenedTurn.current === assistant.id) return
    const sources = messageSources(assistant)
    if (sources.length === 0) return
    autoOpenedTurn.current = assistant.id
    setActiveSlug(sources[0].slug)
  }, [messages])

  const askQuestion = (question: string) => {
    setView('help')
    sendMessage({ text: question })
  }

  const submitTopInput = () => {
    const trimmed = topInput.trim()
    if (!trimmed) return
    askQuestion(trimmed)
    setTopInput('')
  }

  // Clear the conversation back to the screen's suggestions: stop any streaming
  // turn, drop the messages, close the reader, and let the next answer auto-open
  // its top source again.
  const resetChat = () => {
    stop()
    setMessages([])
    setActiveSlug(null)
    setTopInput('')
    autoOpenedTurn.current = null
  }

  const openSupport = () => {
    close()
    openTicket()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close()
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[50%] z-50 flex h-[90vh] max-h-[960px] w-[calc(100%-1.5rem)] max-w-6xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl focus:outline-none data-[state=open]:animate-[dialog-enter_0.15s_ease-out_forwards] data-[state=closed]:animate-[dialog-exit_0.15s_ease-in_forwards]"
        >
          <DialogTitle className="sr-only">Ask CounselPro</DialogTitle>

          <header className="shrink-0 border-b border-border bg-white">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <CounselProMark className="h-5 w-5 shrink-0 text-brand" />
                <span className="hidden truncate text-sm font-semibold text-blue-7 sm:block">
                  Help &amp; support
                </span>
              </div>

              <HelpViewTabs
                view={view}
                onSelect={setView}
                unreadCount={unreadCount}
              />

              <div className="flex justify-end">
                <DialogPrimitive.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-blue-4 transition-colors hover:bg-blue-1 hover:text-blue-7"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </DialogPrimitive.Close>
              </div>
            </div>

            {view === 'help' && (
              <div className="flex items-center gap-3 px-4 pb-4">
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    submitTopInput()
                  }}
                  className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-white px-3 focus-within:border-brand/50"
                >
                  <SearchIcon className="h-4 w-4 shrink-0 text-blue-4" />
                  <input
                    value={topInput}
                    onChange={(event) => setTopInput(event.target.value)}
                    placeholder="Ask about CounselPro…"
                    className="flex-1 bg-transparent py-2.5 text-[0.95rem] text-blue-7 outline-none placeholder:text-blue-4"
                  />
                </form>

                <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                  {surface.label}
                </span>
              </div>
            )}
          </header>

          {view === 'tickets' ? (
            <TicketsPane />
          ) : (
            <div className="grid min-h-0 flex-1 grid-rows-2 md:grid-cols-[minmax(0,44%)_minmax(0,56%)] md:grid-rows-1">
              <AnswerPane
                surface={surface}
                messages={messages}
                status={status}
                error={error}
                onAsk={askQuestion}
                onOpenArticle={setActiveSlug}
                activeSlug={activeSlug}
                onSelectSource={setActiveSlug}
                onStartOver={resetChat}
              />
              <ReaderPane
                slug={activeSlug}
                surface={surface}
                onOpenArticle={setActiveSlug}
              />
            </div>
          )}

          {view === 'help' && (
            <footer className="shrink-0 border-t border-border bg-white px-4 py-3">
              <p className="mb-2 text-center text-xs text-muted-foreground">
                Still stuck? A person on our team can pick this up.
              </p>
              <button
                type="button"
                onClick={openSupport}
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand/90"
              >
                <TicketIcon className="h-5 w-5" />
                Submit a support ticket
              </button>
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
